import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import Datagrid from '../ui/Datagrid';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useLocation } from 'react-router-dom';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import { firebaseGridColumnGenerator } from '../../utils/firebaseGridColumnGenerator';
import { getFirebaseDrillData } from '../../store/actions/firebaseDrill';
import { transformConditions, validateConditions } from '../../utils/firebaseValidations';
import FirebaseFilters from '../ui/FirebaseFilters';

const FirebaseDrill = () => {
  const dispatch = useDispatch();
  const [columns, setColumns] = useState([]);
  const firebaseData = useSelector((state) => state.firebaseDrill);
  const apiRef = useGridApiRef();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [limitValue, setLimitValue] = useState(10);
  const [fieldsSelectionList, setFieldsSelectionList] = useState([]);
  const [conditions, setConditions] = useState([
    ['', '', ''] // field, operator, value
  ]);
  const location = useLocation();
  const [collection, setCollection] = useState(null);

  const getInitialLoadData = () => {
    setConditions([
      ['', '', '']
    ]);
    const searchParams = new URLSearchParams(location.search);
    const body = {
      collection: searchParams.get('col'),
      params: {
        logic: 'and',
        limit: limitValue,
        operators: [],
      },
    };
    dispatch(getFirebaseDrillData(body));
    setCollection(searchParams.get('col'));
  };

  useEffect(() => {
    getInitialLoadData();
  }, [location.search]);

  useEffect(() => {
    if (firebaseData.executeSearch) {
      const hasValidConditions = conditions.length > 1 || (conditions.length === 1 && conditions[0].some(val => val !== ''));

      if (hasValidConditions) {
        const result = validateConditions(conditions);

        if (!result.isValid) {
          dispatch({
            type: REDUX_ACTIONS.FIREBASE_DRILL_COLLECTION_DATA_SEARCH_RESET
          });
          dispatch(
            enqueueSnackbar(
              result.message,
              'error',
              new Date().getTime() + Math.random()
            )
          );
          return;
        }
      }

      const conditionsPayload = transformConditions(conditions, firebaseData?.filterByDetail);

      const params = {
        collection,
        params: {
          logic: 'and',
          limit: limitValue,
          operators: hasValidConditions ? conditionsPayload : [],
        },
      };

      dispatch(getFirebaseDrillData(params));
    }
  }, [firebaseData.executeSearch]);

  useEffect(() => {
    if (!firebaseData.loading && firebaseData.data.length > 0) {
      let columnsList = [];
      columnsList = firebaseGridColumnGenerator(
        [],
        firebaseData.data,
      );

      setColumns(columnsList);
      apiRef.current.updateColumns(columnsList);
      setFieldsSelectionList(columnsList.filter(item => (item.field !== 'lastUpdate' && item.field !== 'grid_id' && item.field !== 'picture')).map(item => item.field));
    }
  }, [firebaseData.data]);

  useEffect(() => {
    if (apiRef.current) {
      const currentColumnVisibilityModel = columns.map((column) => ({
        field: column.field,
        visible: true,
        hide: false
      }));
      setColumnVisibilityModel(currentColumnVisibilityModel);
      apiRef.current.setColumnVisibilityModel(columnVisibilityModel);
    }
    const fieldsToHide = ['grid_id', '_id', 'picture'];
    const columnsToHide = columns.filter((column) => {
      return fieldsToHide.includes(column.field);
    });
    columnsToHide.forEach((column) => {
      apiRef.current.setColumnVisibility(column.field, false);
    });
  }, [apiRef, columns]);

  return (
    <>
      <FirebaseFilters
        conditions={conditions}
        setConditions={setConditions}
        firebaseData={firebaseData}
        fieldsSelectionList={fieldsSelectionList}
        getInitialLoadData={getInitialLoadData}
        setLimitValue={setLimitValue}
        limitValue={limitValue}
      />
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <Datagrid
            key={columns.map(col => col.field).join(',')}
            apiRef={apiRef}
            data={firebaseData.data}
            columns={columns}
            loading={firebaseData.loading}
            getRowId={(row) => row?.grid_id}
            hideFooter={false}
            autoHeight
            getRowHeight={() => 'auto'}
            experimentalFeatures={{ newEditingApi: true }}
            sx={{
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                p: 1
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '0.9rem',
                fontWeight: '900 !important',
              },
            }}
            toolbar={{
              options: {
                columns: true,
                filters: true,
                density: true,
                export: false,
              },
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default FirebaseDrill;
